/**
 * `/rooms/$roomId?seq=` (lane CHATWEB1): one room. `?seq=` is the deep link of notifications and search hits. Below
 * 768 px the room fills the screen with a back button; from 768 px the room list stays beside it (the `/rooms` layout,
 * rooms.tsx, keeps it mounted from room to room).
 */
import { useEffect } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { Skeleton } from '@algoboo/ui'
import { usePageMeta } from '../lib/meta'
import { SignInPrompt, useSession } from '../components/session'
import { RoomView } from '../components/rooms/RoomView'
import { roomFeedQuery, roomMembersQuery, roomQuery } from '../lib/api/rooms'
import { prefetch, prefetchSignedIn } from '../lib/route-data'

export const Route = createFileRoute('/rooms/$roomId')({
  params: {
    parse: (p) => ({ roomId: z.string().uuid().parse(p.roomId) }),
    stringify: (p) => ({ roomId: p.roomId }),
  },
  validateSearch: z.object({ seq: z.coerce.number().int().positive().optional().catch(undefined), invite: z.string().regex(/^[A-Za-z0-9_-]{12,32}$/).optional().catch(undefined) }),
  loaderDeps: ({ search }) => ({ seq: search.seq }),
  // the room, and once the room says it may be read, its messages and members (the list is the layout's)
  loader: ({ context: { queryClient: qc }, params: { roomId }, deps }) =>
    prefetchSignedIn(qc, (me) => [
      qc.ensureQueryData(roomQuery(roomId)).then((room) => {
        if (room.me || room.privacy === 'public') prefetch(qc.ensureQueryData(roomFeedQuery(qc, roomId, deps.seq ?? null, me.user.id)), qc.ensureInfiniteQueryData(roomMembersQuery(roomId)))
      }),
    ]),
  component: RoomPage,
})

function RoomPage() {
  usePageMeta('Room')
  const { roomId } = Route.useParams()
  const { seq, invite } = Route.useSearch()
  const navigate = useNavigate()
  const { me, loading } = useSession()
  // a direct invite's link (`?invite=`, lane CHATCORE1's notification) opens the invite page
  useEffect(() => {
    if (invite) void navigate({ to: '/rooms/invite/$code', params: { code: invite }, replace: true })
  }, [invite, navigate])
  if (loading) return <Skeleton className="m-4 h-[60vh] rounded-xl" />
  if (!me)
    return (
      <div className="mx-auto max-w-[720px] px-4 pt-6">
        <SignInPrompt title="A room" body="Rooms are for people who signed in. Sign in to read and write here." />
      </div>
    )
  return (
    <div className="min-h-0">
      <RoomView roomId={roomId} focusSeq={seq ?? null} meUserId={me.user.id} onBack={() => void navigate({ to: '/rooms' })} />
    </div>
  )
}
